import { useEffect } from "react";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import { authClient, invalidateSession } from "@/lib/auth-client";
import { getDesktopBridge } from "@/lib/desktop";
import { queryClient } from "@/lib/trpc";
import { router } from "@/router";

async function completeSignIn(token: string): Promise<void> {
	const { error } = await authClient.oneTimeToken.verify({ token });
	if (error) {
		track("auth.sign_in_failed", { method: "desktop_browser" });
		toast.error(error.message ?? "Sign-in link expired. Please try again.");
		return;
	}
	track("auth.signed_in", { method: "desktop_browser" });
	invalidateSession();
	queryClient.clear();
	toast.success("Signed in");
	await router.navigate({ to: "/" });
}

/**
 * Desktop only: finishes browser-based sign-in. The website hands a one-time
 * token back through a pluck://auth link; verifying it sets this app's
 * session cookie.
 */
export function DesktopAuthListener() {
	useEffect(() => {
		const bridge = getDesktopBridge();
		if (!bridge) {
			return;
		}
		const handle = (token: string) => {
			completeSignIn(token).catch(() => {
				toast.error("Sign-in failed. Please try again.");
			});
		};
		const unsubscribe = bridge.onAuthToken(handle);
		bridge
			.takePendingAuthToken()
			.then((token) => {
				if (token) {
					handle(token);
				}
			})
			.catch(() => {
				// No pending token.
			});
		return unsubscribe;
	}, []);
	return null;
}
